import React, { useState, useEffect, useRef } from 'react';
import './LoadingScreen.css';

const CATCHING_PHRASES = [
  "Moonwalking to the destination... 🚶‍♂️✨",
  "Crafting your dream route... 🎨✈️",
  "Iterating for the best views... 🔄🏔️",
  "Packing your virtual suitcase... 🧳🎒",
  "Brewing fresh travel plans... ☕🗺️",
  "Polishing the map pins... 📍✨",
  "Consulting local guides... 🗣️🏝️",
  "Searching for hidden gems... 💎🗺️",
  "Calculating road trip vibes... 🚗🎶",
  "Securing window seats... 🪟✈️",
  "Plotting shortcuts... 🗺️💨",
  "Assembling your adventure... 🧩🧭",
  "Adding extra sunshine... ☀️🌴"
];

const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

// onComplete fires after `minDurationMs` — just long enough for the flight
// animation to read as an intentional brand moment, not a forced wait. This
// used to be a hardcoded 3800ms with no relation to how long anything
// actually took to load; callers that need to wait for real data should
// gate on their own loading state and only mount this for the visual, not
// rely on onComplete as a data-ready signal.
const LoadingScreen = ({ onComplete, minDurationMs = 900 }) => {
  const [shuffledPhrases, setShuffledPhrases] = useState([]);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  useEffect(() => {
    setShuffledPhrases(shuffleArray(CATCHING_PHRASES));
  }, []);

  useEffect(() => {
    if (shuffledPhrases.length === 0) return;
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % shuffledPhrases.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [shuffledPhrases]);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    if (!onCompleteRef.current) return undefined;
    const completeTimer = setTimeout(() => {
      if (onCompleteRef.current) onCompleteRef.current();
    }, minDurationMs);
    return () => clearTimeout(completeTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDurationMs]); // intentionally excludes onComplete — see ref above

  return (
    <div className="loading-screen">
      <div className="loading-content">
        <h1 className="loading-title">JOURNIM</h1>

        <div className="route-loader" aria-hidden="true">
          <svg className="route-loader-svg" viewBox="0 0 220 64" preserveAspectRatio="xMidYMid meet">
            <path
              className="route-path"
              d="M14,50 C60,6 160,6 206,50"
            />
            <circle className="route-dot route-dot-start" cx="14" cy="50" r="4.5" />
            <circle className="route-dot route-dot-end" cx="206" cy="50" r="4.5" />
          </svg>
          <span className="route-plane" role="img" aria-label="Traveling">✈️</span>
        </div>

        <p className="loading-subtitle">{shuffledPhrases[currentPhraseIndex] || "Preparing your journey..."}</p>
      </div>
    </div>
  );
};

export default LoadingScreen;
